import { Cookies, date } from 'quasar';
import { defineStore } from 'pinia';
import {
  ConsumptionReadResponse,
  ForgotPasswordUpdateRequest,
  ForgotPasswordUpdateResponse,
  InviteConfirmRequest,
  InviteConfirmResponse,
  LoginServerResponse,
  UserServerResponse,
  UserSettingsType,
  customAxiosRequestConfig,
} from 'src/core/types/user';
import { useAppStore } from 'src/core/stores/app';
import { useApiStore } from 'src/core/stores/api';
import router from 'src/router';
import { useNotificationsStore } from 'src/core/stores/notification';
import { useAnalyticsStore } from 'src/core/stores/analytics';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import { UserType } from 'src/modules/userAccess/types';
import { useUserEnumsStore } from '../enum/user';
import {
  initializeIntercom,
  shutdownIntercom,
} from 'src/core/services/intercom';
import { useDatasetEnumsStore } from 'src/core/stores/enum/dataset';
import { EnumWithType } from 'src/core/types/enum';
import { usePermissionStore } from 'src/core/stores/permission';
import { ROLE_IDS } from 'src/modules/userAccess/types';
import { MFAErrorCode } from 'src/core/types/mfa';
import { i18n } from 'src/boot/i18n';

type UserStore = {
  email: string;
  access_token: string;
  refresh_token: string;
  settings: UserSettingsType;
  user: UserServerResponse | undefined;
  consumptionPollingInterval: ReturnType<typeof setInterval> | null;
};

/**
 * User Store
 */
export const useUserStore = defineStore('user', {
  state: () =>
    ({
      email: '',
      settings: {
        formats: {
          date_full: 'YYYY-MM-DD HH:mm',
          date_short: 'YYYY-MM-DD',
        },
      },
      access_token: localStorage.getItem('Meridia_verify_access_token') || '',
      refresh_token: localStorage.getItem('Meridia_verify_refresh_token') || '',
      user: undefined,
      consumptionPollingInterval: null,
    } as UserStore),

  getters: {
    /**
     * Format the number to users country
     * @returns formatted number
     */
    formatNumber:
      () =>
      (number: number | string): string => {
        return new Intl.NumberFormat().format(Number(number));
      },

    getPlanCurrentLimit: (state) => {
      if (!state.user?.plan || !state.user?.plan?.consumption_limit) return 0;
      return (
        (state.user?.usage_consumption / state.user?.plan?.consumption_limit) *
        100
      );
    },

    /**
     * Format the date
     * @param state - The user store state
     * @returns The formatted date
     */
    formatDate:
      (state) =>
      (d: string | Date, customFormat?: string, short?: boolean): string => {
        let format_to_be_used = 'YYYY-MM-DD HH:mm';
        if (customFormat) {
          format_to_be_used = customFormat;
        } else if (short && state.settings.formats.date_short) {
          format_to_be_used = state.settings.formats.date_short;
        } else if (state.settings.formats.date_full) {
          format_to_be_used = state.settings.formats.date_full;
        }
        return date.formatDate(new Date(d), format_to_be_used);
      },
    /**
     * Check if the user is signed in
     * @param state - The user store state
     * @returns True if the user is signed in, false otherwise
     */
    checkSigned: (state) => state.access_token,

    /**
     * Get the company of the user
     * @returns The name of the user's company, or null if not found
     */
    getUserCompany(state): string | null {
      if (!state.user) return null;
      const companyName = state.user?.customer?.company_name;
      if (companyName) {
        return companyName;
      }
      if (state.user?.supplier?.customer)
        return state.user.supplier.customer?.company_name;
      return null;
    },

    getUserRole:
      () =>
      (user: UserServerResponse | undefined): string => {
        const userEnumsStore = useUserEnumsStore();
        if (!user?.role_ids) return '';
        if (user?.supplier && user?.role_ids[0])
          return i18n.global.t('app.roles.supplier_creator');
        else if (user?.role_ids[0] === ROLE_IDS.MERIDIA_ADMIN)
          return i18n.global.t('app.roles.meridia_admin');
        else
          return userEnumsStore.getFormatedEnumValue(String(user?.role_ids[0]));
      },
    getRole: () => {
      const userEnumsStore = useUserEnumsStore();
      return (user: UserServerResponse | undefined): string => {
        switch (true) {
          case user?.supplier && user?.role_ids[0] !== undefined:
            return 'Supplier Creator';
          case user?.role_ids[0] === ROLE_IDS.MERIDIA_ADMIN:
            return 'Meridia admin';
          default:
            return userEnumsStore.getFormatedEnumValue(
              String(user?.role_ids[0])
            );
        }
      };
    },
    shouldShowEntityFilter(state): boolean {
      return state.user?.supplier ? false : true;
    },
    /**
     * Get the full name of the user
     * @returns The full name of the user
     */
    getFullUserName(state): string {
      return `${state.user?.first_name} ${state.user?.last_name}`;
    },
    /**
     * Get the full name of the user
     * @returns The full name of the user
     */
    getInitials(state): string {
      const firstNameInitial = state.user?.first_name[0]?.toUpperCase();
      const lastNameInitial = state.user?.last_name[0]?.toUpperCase();

      return `${firstNameInitial}${lastNameInitial}`;
    },

    /**
     * Get the commodities of the customer
     * @param state - The user store state
     * @returns The commodities of the customer
     */
    getCustomerCommodities(state): EnumWithType[] {
      const datasetEnumsStore = useDatasetEnumsStore();
      const permissionStore = usePermissionStore();
      if (permissionStore.permissions.includes('admin')) {
        return datasetEnumsStore.enums.commodities;
      }
      if (state.user?.supplier) {
        return (
          datasetEnumsStore.enums?.commodities?.filter((commodity) =>
            state.user?.supplier?.customer?.commodities.includes(
              commodity.value
            )
          ) ?? []
        );
      }
      return (
        datasetEnumsStore.enums?.commodities?.filter((commodity) =>
          state.user?.customer?.commodities.includes(commodity.value)
        ) ?? []
      );
    },
  },

  actions: {
    /**
     * Login action
     * @param email - The user's email
     * @param password - The user's password
     * @param remember_me - Whether to remember the user's login
     * @returns True if the login is successful, false otherwise
     */
    async login(
      email: string,
      password: string,
      remember_me: boolean,
      mfa_code?: string,
      mfa_challenge_id?: string
    ): Promise<
      | boolean
      | {
          detail: string;
          code?: string;
          mfa_challenge_id?: string;
          enrollment_token?: string;
          remaining_attempts?: number;
          retry_after_seconds?: number;
          is_locked_out?: boolean;
        }
    > {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      const impersonateUserCookie = Cookies.get('impersonateUser');
      let impersonateUser: UserType | null = null;

      if (impersonateUserCookie) {
        try {
          if (typeof impersonateUserCookie === 'string') {
            impersonateUser = JSON.parse(impersonateUserCookie) as UserType;
          } else {
            impersonateUser = impersonateUserCookie as UserType;
          }
        } catch (error) {
          console.warn(
            'Failed to parse impersonateUser cookie in login:',
            error
          );
          Cookies.remove('impersonateUser');
        }
      }

      try {
        const responseLogin = await apiStore.service.post<LoginServerResponse>(
          impersonateUser?.id
            ? `/user/login?impersonate_user=${impersonateUser.id}`
            : '/user/login',
          {
            email,
            password,
            remember_me,
            mfa_code,
            mfa_challenge_id,
          }
        );
        await this.doSignIn(
          responseLogin.data.access_token,
          responseLogin.data.refresh_token
        );
        return true;
      } catch (error: any) {
        this.testConnectivity(import.meta.env.VITE_API_URL);

        console.error('Login Error', {
          error: error.message,
          code: error.code,
          config: JSON.stringify({
            adapter: JSON.stringify(error.config.adapter),
            baseURL: error.config.baseURL,
            headers: JSON.stringify(error.config.headers),
            method: error.config.method,
            params: JSON.stringify(error.config.params),
            url: error.config.url,
            withCredentials: error.config.withCredentials,
            xsrfCookieName: error.config.xsrfCookieName,
          }),
          request: JSON.stringify(error.request),
          response: JSON.stringify(error.response),
          // Add CORS-specific error details
          isCorsError: error.message.includes('CORS'),
          origin: window.location.origin,
          responseHeaders: error.response?.headers,
          allowOrigin: error.response?.headers?.['access-control-allow-origin'],
          allowMethods:
            error.response?.headers?.['access-control-allow-methods'],
          allowCredentials:
            error.response?.headers?.['access-control-allow-credentials'],
          // Add hostname info
          hostname: new URL(error.config?.baseURL || '').hostname,
        });
        if (error?.response?.data?.detail === 'Impersonation is not allowed') {
          Cookies.remove('impersonateUser');
          // RETRY WITHOUT THE IMPERSONATE
          return await this.login(email, password, remember_me);
        }

        // Handle MFA challenge or enrollment required (403)
        if (error?.response?.status === 403 && error?.response?.data?.detail) {
          const detail = error.response.data.detail;
          // Check if it's an MFA challenge (detail is an object with code: "MFA_REQUIRED")
          if (
            typeof detail === 'object' &&
            detail.code === MFAErrorCode.MFA_REQUIRED
          ) {
            return {
              detail: detail.message || i18n.global.t('mfa.login.mfa_required'),
              code: MFAErrorCode.MFA_REQUIRED,
              mfa_challenge_id: detail.mfa_challenge_id,
            };
          }
          // Check if MFA enrollment is required
          if (
            typeof detail === 'object' &&
            detail.code === MFAErrorCode.MFA_ENROLLMENT_REQUIRED
          ) {
            return {
              detail:
                detail.message || i18n.global.t('mfa.login.enrollment_required'),
              code: MFAErrorCode.MFA_ENROLLMENT_REQUIRED,
              enrollment_token: detail.enrollment_token,
            };
          }
          // Check if MFA code is invalid (with remaining attempts info)
          if (
            typeof detail === 'object' &&
            detail.code === MFAErrorCode.INVALID_MFA_CODE
          ) {
            return {
              detail: detail.message || i18n.global.t('mfa.login.invalid_code'),
              code: MFAErrorCode.INVALID_MFA_CODE,
              remaining_attempts: detail.remaining_attempts,
            };
          }
        }

        // Handle rate limit exceeded (429)
        if (error?.response?.status === 429 && error?.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (
            typeof detail === 'object' &&
            detail.code === MFAErrorCode.RATE_LIMIT_EXCEEDED
          ) {
            return {
              detail:
                detail.message || i18n.global.t('mfa.login.rate_limit_exceeded'),
              code: MFAErrorCode.RATE_LIMIT_EXCEEDED,
              retry_after_seconds: detail.retry_after_seconds,
              is_locked_out: detail.is_locked_out,
            };
          }
        }

        if (error?.response?.data?.detail) {
          const detail = error.response.data.detail;
          // Handle object detail format
          if (typeof detail === 'object' && detail.message) {
            return { detail: detail.message, code: detail.code };
          }
          return { detail: detail };
        }
        handleError('Login error:', error);
        return false;
      }
    },
    /**
     * Performs the sign-in operation using the provided access token and refresh token.
     * @param access_token - The access token for authentication.
     * @param refresh_token - The refresh token for authentication.
     */
    async doSignIn(access_token: string, refresh_token: string) {
      const appStore = useAppStore();
      const { handleError } = useErrorHandling();
      try {
        await this.setToken('access_token', access_token);
        await this.setToken('refresh_token', refresh_token);
        await this.getUserInfo();
        this.startConsumptionPolling();

        await appStore.initializeNavigationItems();
        await appStore.fillUnAuthorizedList();

        await appStore.getAllEnums();
        await this.clearLocalStorage();
      } catch (error) {
        handleError('doSignIn error:', error);
        throw error;
      }
    },
    /**
     * Get user information
     */
    async getUserInfo() {
      const { handleError } = useErrorHandling();
      const apiStore = useApiStore();

      try {
        const responseUserInfo =
          await apiStore.service.post<UserServerResponse>('/user/read/self');
        this.$patch({ user: responseUserInfo.data });
        const analyticsStore = useAnalyticsStore();
        await analyticsStore.identifyUser();
        await initializeIntercom(responseUserInfo.data);
      } catch (error: any) {
        if (apiStore.checkErrorResponse(error.response, 401)) {
          await this.signOut(true);
          return;
        }
        handleError('User info error', error);
      }
    },

    /**
     * Refresh the access token
     * @param payload - The payload containing the refresh token
     * @returns The response data
     */
    async refreshToken(payload: { refresh_token: string }) {
      try {
        const apiStore = useApiStore();
        const refreshResponse =
          await apiStore.service.post<LoginServerResponse>(
            '/user/refresh',
            payload,
            customAxiosRequestConfig
          );

        await this.setToken('access_token', refreshResponse.data.access_token);
        await this.setToken(
          'refresh_token',
          refreshResponse.data.refresh_token
        );

        return refreshResponse.data;
      } catch (e) {
        await this.signOut(true);
      }
    },

    /**
     * Sign out the user
     * @param isRefresh - Whether the sign out is due to a refresh token failure
     */
    async impersonateLogout(isRefresh = false) {
      const { handleError } = useErrorHandling();
      this.stopConsumptionPolling();

      try {
        if (this.access_token === '') {
          throw Error('LogOut: token is undefined!');
        }
        if (!isRefresh) {
          const apiStore = useApiStore();
          await apiStore.service.get('/user/logout');
        }
        this.deleteAccessTokens();
        this.clearLocalStorage();
        this.clearSessionStorage();
        useNotificationsStore().notifications = [];
        router.push({ name: 'login' });
      } catch (error) {
        handleError('Impersonate logout error', error);
      }
    },
    /**
     * Sign out the user
     * @param isRefresh - Whether the sign out is due to a refresh token failure
     */
    async signOut(isRefresh = false) {
      const { handleError } = useErrorHandling();
      this.stopConsumptionPolling();
      const impersonateUser = Cookies.get('impersonateUser');
      const analyticsStore = useAnalyticsStore();
      analyticsStore.logoutMixpanel();
      await shutdownIntercom();
      try {
        if (this.access_token === '') {
          throw Error('LogOut: token is undefined!');
        }
        if (!isRefresh) {
          const apiStore = useApiStore();
          await apiStore.service.get('/user/logout');
        }
        this.deleteAccessTokens();
        this.clearLocalStorage();
        this.clearSessionStorage();
        useNotificationsStore().notifications = [];
        router.push({ name: 'login' });
      } catch (error) {
        this.deleteAccessTokens();
        this.clearLocalStorage();
        this.clearSessionStorage();
        handleError('Signout error', error);
      } finally {
        if (impersonateUser) {
          Cookies.remove('impersonateUser');
        }
      }
    },

    /**
     * Clears the local storage by removing all items except those that start with 'ajs_'.
     */
    clearLocalStorage() {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (
          key &&
          !key.startsWith('ajs_') &&
          !key.startsWith('Meridia_verify')
        ) {
          localStorage.removeItem(key);
        }
      }
    },

    /**
     * Clears the session storage to remove all temporary data like table filters.
     */
    clearSessionStorage() {
      sessionStorage.clear();
    },

    /**
     * Delete the access tokens
     */
    deleteAccessTokens() {
      localStorage.removeItem('Meridia_verify_access_token');
      localStorage.removeItem('Meridia_verify_refresh_token');
      this.access_token = '';
      this.refresh_token = '';
    },

    /**
     * Method to set tokens to ensure context remains consistent.
     * @param key - The type of token: 'access_token' or 'refresh_token'
     * @param token - The token value
     */
    setToken(key: 'access_token' | 'refresh_token', token: string) {
      if (key === 'access_token') {
        localStorage.setItem('Meridia_verify_access_token', token);
        this.$patch({ access_token: token });
      } else if (key === 'refresh_token') {
        localStorage.setItem('Meridia_verify_refresh_token', token);
        this.$patch({ refresh_token: token });
      }
    },

    /**
     * Verifies an invite token.
     * @param token - The invite token to verify.
     * @returns A boolean indicating whether the verification was successful or not.
     */
    async verifyInvite(token: string) {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        await apiStore.service.post<boolean>('/user/invite/verify', {
          token,
        });
        return true;
      } catch (error) {
        handleError('Error on verifyInvite', error);
        return false;
      }
    },

    /**
     * Fetches the latest consumption data from the server.
     * Updates the user object with new plan and consumption values.
     */
    async refetchConsumption() {
      if (!this.access_token) return;

      const apiStore = useApiStore();
      try {
        const response = await apiStore.service.post<ConsumptionReadResponse>(
          '/user/consumption/read'
        );
        if (this.user) {
          this.$patch({
            user: {
              ...this.user,
              plan: response.data.plan,
              usage_consumption: response.data.usage_consumption,
              over_consumption_limit: response.data.over_consumption_limit,
            },
          });
        }
      } catch (error: any) {
        // Only stop polling on 401 (after refresh token also failed)
        // Other errors (network, 500s) are transient - just skip this poll cycle
        if (error?.response?.status === 401) {
          this.stopConsumptionPolling();
        }
      }
    },

    /**
     * Starts polling for consumption data every 5 minutes.
     */
    startConsumptionPolling() {
      this.stopConsumptionPolling();
      this.consumptionPollingInterval = setInterval(() => {
        this.refetchConsumption();
      }, 60 * 1000);
    },

    /**
     * Stops the consumption polling interval.
     */
    stopConsumptionPolling() {
      if (this.consumptionPollingInterval) {
        clearInterval(this.consumptionPollingInterval);
        this.consumptionPollingInterval = null;
      }
    },

    /**
     * Confirms the invitation for a user.
     * @param payload - The payload containing the invitation confirmation details.
     * @returns A Promise that resolves to the response data if the invitation confirmation is successful,
     * or `false` if it fails.
     */
    async inviteConfirm(
      payload: InviteConfirmRequest
    ): Promise<
      | { status: true; data: InviteConfirmResponse }
      | { status: false; message?: string }
    > {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();

      try {
        const response = await apiStore.service.post<InviteConfirmResponse>(
          '/user/invite/confirm',
          {
            payload,
          }
        );
        return { status: true, data: response.data };
      } catch (error: any) {
        if (error?.code === 'ERR_BAD_REQUEST') {
          if (Array.isArray(error.response.data.detail)) {
            return {
              status: false,
              message: error.response.data.detail[0].msg,
            };
          }
          return { status: false, message: error.response.data.detail };
        }
        handleError('Error on Forgot password', error);
        return { status: false };
      }
    },

    /**
     * Sends a forgot password request to the server.
     *
     * @param email - The email address of the user.
     * @returns A boolean indicating whether the forgot password request was successful.
     */
    async forgotPassword(
      email: string
    ): Promise<{ status: boolean; message?: string }> {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        await apiStore.service.post<boolean>('/user/forgot_password', {
          email,
        });
        return { status: true };
      } catch (error: any) {
        if (error?.code === 'ERR_BAD_REQUEST') {
          return { status: false, message: error.response.data.detail };
        }
        handleError('Error on Forgot password', error);
        return { status: false };
      }
    },

    /**
     * Verifies the password reset token.
     * @param token - The password reset token.
     * @returns A boolean indicating whether the verification was successful.
     */
    async forgotPasswordVerify(token: string) {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        await apiStore.service.post<boolean>('/user/forgot_password/verify', {
          token,
        });
        return true;
      } catch (error) {
        handleError('Error on Forgot Password verify', error);
        return false;
      }
    },

    /**
     * Updates the user's password after a forgot password request.
     * @param payload - The request payload containing the necessary information for password update.
     * @returns A Promise that resolves to the response data if the update is successful, or false otherwise.
     */
    async forgotPasswordUpdate(
      payload: ForgotPasswordUpdateRequest
    ): Promise<
      | { status: true; data: ForgotPasswordUpdateResponse }
      | { status: false; message?: string }
    > {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const response =
          await apiStore.service.post<ForgotPasswordUpdateResponse>(
            '/user/forgot_password/update',
            {
              payload,
            }
          );
        return { status: true, data: response.data };
      } catch (error: any) {
        if (error?.code === 'ERR_BAD_REQUEST') {
          if (Array.isArray(error.response.data.detail)) {
            return {
              status: false,
              message: error.response.data.detail[0].msg,
            };
          }
          return { status: false, message: error.response.data.detail };
        }
        handleError('Error on Forgot password', error);
        return { status: false };
      }
    },

    /**
     * Checks if SSO is enabled for a specific email
     * @param email - User's email address
     * @returns Boolean indicating if SSO is enabled
     */
    async checkSsoEnabled(email: string): Promise<boolean> {
      const { handleError } = useErrorHandling();

      try {
        const apiStore = useApiStore();
        const response = await apiStore.service.get('/sso/enabled', {
          params: { email },
        });

        return response.data?.enabled === true;
      } catch (error) {
        handleError('Error checking SSO status:', error);
        return false;
      }
    },

    /**
     * Initiates SSO authentication flow
     * @param email - User's email address
     */
    redirectToSsoAuth(email: string) {
      if (!email) return;

      const origin = window.location.origin;
      const impersonateUser = Cookies.get('impersonateUser');

      let url = `${
        import.meta.env.VITE_API_URL
      }/sso/auth?email=${encodeURIComponent(email)}&origin=${encodeURIComponent(
        origin
      )}`;

      // Add impersonation parameter if present
      if (impersonateUser) {
        try {
          let parsedUser: UserType;

          // Handle case where cookie might already be parsed as object
          if (typeof impersonateUser === 'string') {
            parsedUser = JSON.parse(impersonateUser) as UserType;
          } else {
            parsedUser = impersonateUser as UserType;
          }

          if (parsedUser?.id) {
            url += `&impersonate_user=${encodeURIComponent(
              parsedUser.id.toString()
            )}`;
          }
        } catch (error) {
          console.warn('Failed to parse impersonateUser cookie:', error);
        }
      }

      window.location.href = url;
    },

    /**
     * Processes SSO redirect with tokens
     * @param access_token - Access token from SSO provider
     * @param refresh_token - Refresh token from SSO provider
     * @returns True if successful, false otherwise
     */
    async processSsoRedirect(
      access_token: string,
      refresh_token: string
    ): Promise<boolean> {
      const { handleError } = useErrorHandling();

      try {
        if (!access_token || !refresh_token) {
          throw new Error('Missing required tokens');
        }

        await this.doSignIn(access_token, refresh_token);

        // Clean up impersonation cookie after successful SSO login
        // (it's now encoded in the tokens from backend)
        Cookies.remove('impersonateUser');

        return true;
      } catch (error) {
        handleError('SSO authentication failed:', error);
        return false;
      }
    },

    async testConnectivity(url) {
      try {
        // Test basic reachability (ignores CORS)
        await fetch(url, { mode: 'no-cors' });
        console.log('✓ Server reachable');

        // Test if it's a CORS issue
        try {
          await fetch(url);
          console.log('✓ CORS is properly configured');
        } catch (corsError) {
          console.log('✗ CORS issue detected');
        }
      } catch (error) {
        console.log('✗ Server unreachable - likely DNS or certificate issue');

        // Try resolving DNS and testing direct IP access
        const hostname = new URL(url).hostname;
        try {
          const response = await fetch(
            `https://1.1.1.1/dns-query?name=${hostname}`,
            {
              headers: { accept: 'application/dns-json' },
            }
          );
          const dnsResult = await response.json();
          const ip = dnsResult.Answer?.[0]?.data;

          if (ip) {
            try {
              await fetch(`http://${ip}`, {
                mode: 'no-cors',
                headers: { Host: hostname },
              });
              console.log(
                '✓ IP direct access works - likely DNS resolution issue'
              );
            } catch (ipError) {
              console.log(
                '✗ IP direct access fails - likely network/firewall issue'
              );
            }
          } else {
            console.log('✗ DNS resolution failed');
          }
        } catch (dnsError) {
          console.log('✗ DNS lookup failed - cannot test IP directly');
        }
      }
    },
  },
});
